import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaGripVertical, FaExternalLinkAlt, FaCopy, FaShare } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from '../../services/axiosConfig';
import LoadingSpinner from '../../components/LoadingSpinner';

const TruncatedDescription = ({ description, className = "" }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 640);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  if (!description) return null;

  const maxChars = isLargeScreen ? 60 : 20;
  const shouldTruncate = description.length > maxChars;
  const displayText = isExpanded || !shouldTruncate ? description : description.substring(0, maxChars);

  return (
    <div className={className}>
      <p className="text-sm leading-relaxed text-gray-500">
        {displayText}
        {shouldTruncate && !isExpanded && '...'}
        {shouldTruncate && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="ml-1 text-sm font-medium text-orange-600 transition-colors hover:text-orange-700"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </p>
    </div>
  );
};

const LinkManagement = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [linkToToggle, setLinkToToggle] = useState(null);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    description: '',
    isPublic: true
  });

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/auth/links');
      if (response.data.success) {
        setLinks(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching links:', error);
      toast.error('Failed to fetch links');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingLink(null);
    setFormData({ title: '', url: '', description: '', isPublic: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingLink) {
        const response = await axios.patch(`/auth/links/${editingLink._id}`, formData);
        if (response.data.success) {
          setLinks(links.map(link =>
            link._id === editingLink._id ? response.data.data : link
          ));
          toast.success('Link updated successfully');
        }
      } else {
        const response = await axios.post('/auth/links', formData);
        if (response.data.success) {
          setLinks([...links, response.data.data]);
          toast.success('Link created successfully');
        }
      }

      resetForm();
    } catch (error) {
      console.error('Error saving link:', error);
      toast.error('Failed to save link');
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      url: link.url,
      description: link.description || '',
      isPublic: link.isPublic
    });
    setShowModal(true);
  };

  const handleDelete = async (linkId) => {
    if (window.confirm('Are you sure you want to delete this link?')) {
      try {
        const response = await axios.delete(`/auth/links/${linkId}`);
        if (response.data.success) {
          setLinks(links.filter(link => link._id !== linkId));
          toast.success('Link deleted successfully');
        }
      } catch (error) {
        console.error('Error deleting link:', error);
        toast.error('Failed to delete link');
      }
    }
  };

  const handleTogglePublic = (link) => {
    setLinkToToggle(link);
    setShowConfirmModal(true);
  };

  const confirmTogglePublic = async () => {
    if (!linkToToggle) return;

    try {
      const response = await axios.patch(`/auth/links/${linkToToggle._id}`, {
        isPublic: !linkToToggle.isPublic
      });
      if (response.data.success) {
        setLinks(links.map(l =>
          l._id === linkToToggle._id ? { ...l, isPublic: !linkToToggle.isPublic } : l
        ));
        toast.success(`Link ${!linkToToggle.isPublic ? 'made public' : 'made private'}`);
      }
    } catch (error) {
      console.error('Error updating link:', error);
      toast.error('Failed to update link');
    } finally {
      setShowConfirmModal(false);
      setLinkToToggle(null);
    }
  };

  const cancelTogglePublic = () => {
    setShowConfirmModal(false);
    setLinkToToggle(null);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));

    if (dragIndex === dropIndex) return;

    const newLinks = [...links];
    const draggedLink = newLinks[dragIndex];
    newLinks.splice(dragIndex, 1);
    newLinks.splice(dropIndex, 0, draggedLink);

    try {
      const linksWithOrder = newLinks.map((link, index) => ({
        id: link._id,
        order: index + 1
      }));

      await axios.patch('/auth/links/order/update', { links: linksWithOrder });
      setLinks(newLinks);
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update link order');
    }
  };

  const getProfileUrl = () => {
    const raw = (localStorage.getItem('username') || '').trim();
    const username = raw.split(/\s+/)[0] || raw;
    if (!username) return null;
    return `${window.location.origin}/u/${encodeURIComponent(username)}`;
  };

  const copyProfileLink = async () => {
    const profileUrl = getProfileUrl();
    if (!profileUrl) {
      toast.error('Profile link not available. Please log in again.');
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(profileUrl);
        toast.success('Profile link copied to clipboard!');
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = profileUrl;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        toast.success('Profile link copied to clipboard!');
      }
    } catch (err) {
      toast.error('Failed to copy. Please copy the link manually.');
    }
  };

  const shareProfile = async () => {
    const profileUrl = getProfileUrl();
    if (!profileUrl) {
      toast.error('Profile link not available. Please log in again.');
      return;
    }
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'My links on Infuber',
          text: 'Check out my links on Infuber',
          url: profileUrl
        });
        toast.success('Profile shared!');
      } else {
        copyProfileLink();
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        copyProfileLink();
      }
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[240px] w-full items-center justify-center p-4 lg:p-6">
        <LoadingSpinner message="Loading your links..." />
      </div>
    );
  }

  return (
    <div className="w-full p-4 lg:p-6">
      <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:mb-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">Link Management</h1>
            <p className="mt-1 text-sm text-gray-500">Manage your public profile links and share them with your audience.</p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <button
              type="button"
              onClick={copyProfileLink}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 sm:px-4 sm:text-sm"
            >
              <FaCopy className="h-3.5 w-3.5" />
              Copy Profile Link
            </button>
            <button
              type="button"
              onClick={shareProfile}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 sm:px-4 sm:text-sm"
            >
              <FaShare className="h-3.5 w-3.5" />
              Share Profile
            </button>
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-xs font-semibold text-white transition hover:bg-orange-600 sm:text-sm"
            >
              <FaPlus className="h-3.5 w-3.5" />
              Add New Link
            </button>
          </div>
        </div>
      </div>

      {links.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-50">
            <FaPlus className="text-lg text-orange-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">No links yet</h3>
          <p className="mt-1 max-w-md text-sm text-gray-500">
            Create your first link to start building your public profile and sharing your content.
          </p>
          <button
            type="button"
            onClick={() => setShowModal(true)}
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
          >
            <FaPlus className="h-3.5 w-3.5" />
            Create Your First Link
          </button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="divide-y divide-gray-100">
            {links.map((link, index) => (
              <div
                key={link._id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className="p-4 transition hover:bg-gray-50 sm:p-5"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <div className="mt-1 shrink-0 cursor-move text-gray-300">
                      <FaGripVertical />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1.5 flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900 sm:text-base">{link.title}</h3>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold sm:text-xs ${
                          link.isPublic
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {link.isPublic ? 'Public' : 'Private'}
                        </span>
                      </div>
                      <TruncatedDescription description={link.description} className="mb-2" />
                      <div className="flex items-start gap-2 rounded-xl bg-gray-50 px-3 py-2">
                        <span className="min-w-0 flex-1 break-all text-xs text-gray-600 sm:text-sm">{link.url}</span>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-0.5 shrink-0 text-orange-500 transition hover:text-orange-600"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <FaExternalLinkAlt className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => handleTogglePublic(link)}
                      className="rounded-lg p-2 text-gray-400 transition hover:bg-green-50 hover:text-green-600"
                      title={link.isPublic ? 'Make Private' : 'Make Public'}
                    >
                      {link.isPublic ? <FaEye /> : <FaEyeSlash />}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleEdit(link)}
                      className="rounded-lg p-2 text-gray-400 transition hover:bg-blue-50 hover:text-blue-600"
                      title="Edit Link"
                    >
                      <FaEdit />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(link._id)}
                      className="rounded-lg p-2 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                      title="Delete Link"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => e.target === e.currentTarget && resetForm()}
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900 sm:text-lg">
                {editingLink ? 'Edit Link' : 'Add New Link'}
              </h2>
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg px-2 text-xl leading-none text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-gray-700 sm:text-sm">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-700 transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                    placeholder="e.g., My Amazon Store"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-gray-700 sm:text-sm">URL *</label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-700 transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                    placeholder="https://example.com"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-gray-700 sm:text-sm">Description (Optional)</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-700 transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                    placeholder="Optional description for your link"
                    rows="3"
                    maxLength={200}
                  />
                  <div className="mt-1 text-right text-xs text-gray-400">{formData.description.length}/200</div>
                </div>
                <div className="flex items-center rounded-xl bg-gray-50 px-3 py-3">
                  <input
                    type="checkbox"
                    id="isPublic"
                    checked={formData.isPublic}
                    onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                  <label htmlFor="isPublic" className="ml-3 text-sm font-medium text-gray-700">
                    Make this link public (visible on your profile)
                  </label>
                </div>
              </div>

              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
                >
                  {editingLink ? 'Update Link' : 'Create Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmModal && linkToToggle && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => e.target === e.currentTarget && cancelTogglePublic()}
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-5 text-center shadow-2xl sm:p-6">
            <h2 className="text-lg font-bold text-gray-900">
              {linkToToggle.isPublic ? 'Make Link Private' : 'Make Link Public'}
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Are you sure you want to {linkToToggle.isPublic ? 'make private' : 'make public'} this link?
            </p>
            <p className="mt-1 text-sm font-medium text-gray-800">"{linkToToggle.title}"</p>
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={cancelTogglePublic}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmTogglePublic}
                className="rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
              >
                {linkToToggle.isPublic ? 'Make Private' : 'Make Public'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkManagement;
